import { useTranslations } from "@zapier/i18n";

import {
  Button,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Text,
} from "@zapier/design-system";

import {
  getMessageFromNetworkError,
  useDeleteAuthentication,
} from "@zapier/client";
import { useNotification } from "@zapier/utils";
import { FlexSpacer } from "@zapier/shared";

interface Props {
  connectionTitle: string;
  id?: number;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

interface ContentProps extends Props {
  onSubmit: () => void;
}

function ConnectionDeleteContent({
  connectionTitle,
  onSubmit,
  setIsOpen,
}: ContentProps) {
  const t = useTranslations("connections-delete-modal");

  return (
    <ModalDialogRoot isOpen={true} onOpenChange={setIsOpen}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the form deliberately stops click/keydown propagation to keep the modal open and submit on Enter */}
      <form
        onSubmit={onSubmit}
        onClick={(event) => event.stopPropagation()} // prevent the modal from closing when clicking inside the form
        onKeyDown={
          // if the enter key is being pressed submit the form
          (event) => {
            return event.key === "Enter" && onSubmit();
          }
        }
      >
        <ModalDialogHeader textAlign="start">
          {t("header.delete-title", { connectionTitle })}
        </ModalDialogHeader>

        <ModalDialogBody textAlign="start">
          {t("body.confirmation-message")}
        </ModalDialogBody>

        <ModalDialogFooter>
          <Button
            variant="tertiary"
            onPress={() => setIsOpen(false)}
            type="button"
          >
            {t("buttons.cancel")}
          </Button>
          <FlexSpacer />
          <Button onPress={onSubmit} type="submit" variant="danger">
            {t("buttons.delete")}
          </Button>
        </ModalDialogFooter>
      </form>
    </ModalDialogRoot>
  );
}

export default function ConnectionDelete({
  connectionTitle,
  id,
  isOpen,
  setIsOpen,
}: Props) {
  const t = useTranslations("connections-delete-modal");

  const { createNotification } = useNotification();
  const { deleteAuthentication } = useDeleteAuthentication();

  /** This callback needs to happen outside of the modal so that it can
   * still display a notification when the request completes after the modal has closed.
   */
  const submit = () => {
    if (!id) {
      return;
    }

    deleteAuthentication(
      { id },
      {
        onSuccess: () => {
          createNotification({
            message: (
              <Text>
                {t("notifications.success-message", {
                  connectionTitle,
                })}
              </Text>
            ),
            type: "success",
          });
        },
        onError: (error: any) => {
          createNotification({
            title: `Unable to delete "${connectionTitle}"`,
            message: getMessageFromNetworkError(error),
            type: "error",
          });
        },
      },
    );

    setIsOpen(false);
  };

  // Do not display the modal if isOpen==false
  if (!isOpen) {
    return null;
  }

  return (
    <ConnectionDeleteContent
      connectionTitle={connectionTitle}
      id={id}
      isOpen={isOpen}
      onSubmit={submit}
      setIsOpen={setIsOpen}
    />
  );
}
